import React, { FC, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  CircularProgress,
  Snackbar,
  Typography,
} from '@mui/material';

import {
  Pagination,
  ExportFormats,
  ConsumptionExportDto,
  AggregationOptions,
  DeviceGroupDto,
} from '@thingslog/repositories';
import Header from '../../components/header';
import {
  MultiSelectAutocompleteOption,
  MultiSelectAutocompleteWithActions,
} from '../../components/MultiSelectAutocomplete';
import PeriodPicker from '../../components/DatePicker/PeriodPicker';
import TitleHeader from '../../components/TitleHeader/TitleHeader';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { useTranslation } from 'react-i18next';
import ConsumptionTable from './ConsumptionTable';
import { useDispatch, useSelector } from 'react-redux';
import { ReduxState } from '../../reducers';
import { Device } from '@thingslog/repositories';

import DeviceSensorsDto from '@thingslog/repositories/src/deviceSensorsDto/DeviceSensorsDto';

import {
  deviceGroupsQueryClient,
  devicesQueryClient,
  multipleConsumptionExportQueryClient,
} from '../../clients/ReactQueryClients/ReactQueryClients';
import GoogleAnalyticsService from '../../common/GoogleAnalyticsService';
import { GaEventCategory } from '../../common/GaEventCategory';
import { GaEventAction } from '../../common/GaEventAction';
import * as actions from '../../actions/index';
import { GridRowParams } from '@thingslog/ui-components';
import ConnectedDatePicker from '../../components/PeriodPicker/ConnectedDatePicker';
import { PickerPeriod } from '../../model/Pickers/PickerPeriod';
import JwtValidator from '../../common/JwtValidator';

const ConsumptionExport: FC<ConsumptionExportProps> = (props: ConsumptionExportProps) => {
  const { useDevicesData } = useMemo(() => devicesQueryClient, []);
  const { useDeviceGroupsData } = useMemo(() => deviceGroupsQueryClient, []);
  const {
    useMultipleConsumptionExportCSV,
    useMultipleConsumptionExportExcel,
    useMultipleConsumptionExportJSON,
  } = useMemo(() => multipleConsumptionExportQueryClient, []);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const companyId = useSelector((state: ReduxState) => state.company.id);
  const selectedDeviceNumber = useSelector((state: ReduxState) => state.dev.devices[0] || null);
  const toDateTz = useSelector((state: ReduxState) => state.period.toDateTz);
  const fromDateTz = useSelector((state: ReduxState) => state.period.fromDateTz);
  const { determineCompanyIdForUserRole } = useMemo(() => new JwtValidator(), []);
  const currentCompanyId = useMemo(() => determineCompanyIdForUserRole(companyId), [companyId]);

  const [aggregationPeriod, setAggregationPeriod] = useState<AggregationOptions | null>(null);
  const [isConsumptionExportAggregated, setIsConsumptionReportAggregated] =
    useState<boolean>(false);
  const [exportFormat, setExportFormat] = useState<ExportFormats>('Excel');
  const [data, setData] = useState<ConsumptionExportDto[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [selectedDeviceGroups, setSelectedDeviceGroups] = useState<number[]>([]);
  const [selectedDeviceSnackbar, setSelectedDeviceSnackbar] = useState<{
    open: boolean;
    deviceNumber: string | null;
    key: number;
  }>({
    open: false,
    deviceNumber: null,
    key: 0,
  });
  const selectedSensors: number[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];

  const aggregationSelectOptions: Record<AggregationOptions | 'NONE', string> = {
    NONE: t('consumption_export_aggregation_options_none'),
    DAILY: t('consumption_export_aggregation_options_daily'),
    WEEKLY: t('consumption_export_aggregation_options_weekly'),
    MONTHLY: t('consumption_export_aggregation_options_monthly'),
    YEARLY: t('consumption_export_aggregation_options_yearly'),
  };

  const devicesQuery = useDevicesData(currentCompanyId, 0, 9999, null, null, {
    enabled: currentCompanyId !== null,
    onError: () => {
      alert(t('counters_export_error_alert_message'));
    },
    refetchOnWindowFocus: false,
  });
  const deviceGroupsQuery = useDeviceGroupsData(currentCompanyId, {
    enabled: currentCompanyId !== null,
    onError: () => {
      alert(t('counters_export_error_alert_message'));
    },
    refetchOnWindowFocus: false,
  });

  const deviceOptions = useMemo((): MultiSelectAutocompleteOption<string>[] => {
    if (!devicesQuery.data) {
      return [];
    }

    return devicesQuery.data.content.map((device: Device) => {
      const label = device.name ? `${device.name} (${device.number})` : device.number;
      return {
        value: device.number,
        label,
        searchText: `${device.number} ${device.name || ''}`.toLowerCase(),
      };
    });
  }, [devicesQuery.data]);

  const deviceGroupOptions = useMemo((): MultiSelectAutocompleteOption<number>[] => {
    if (!deviceGroupsQuery.data) {
      return [];
    }

    return deviceGroupsQuery.data.map((group: DeviceGroupDto) => ({
      value: group.deviceGroupId,
      label: group.deviceGroupName,
      searchText: `${group.deviceGroupName} ${group.deviceGroupId}`.toLowerCase(),
    }));
  }, [deviceGroupsQuery.data]);

  //mutations for json load and export
  const {
    mutate: jsonMutate,
    isLoading: isJSONLoading,
    isSuccess: isJSONSuccess,
  } = useMultipleConsumptionExportJSON({
    onSuccess: (data: ConsumptionExportDto[]) => {
      setData(data);
    },
    onError: () => {
      alert(t('counters_export_error_alert_message'));
    },
  });

  const {
    mutate: exportMutateCSV,
    isLoading: isExportingCSV,
    isSuccess: isExportCSVSuccess,
  } = useMultipleConsumptionExportCSV({
    onSuccess: (data: ArrayBuffer) => {
      let fileExtension = 'csv';
      const fileName = `ConsumptionExport-${exportFormat}-from-${fromDateTz.toISOString()}-to-${toDateTz.toISOString()}.${fileExtension}`;
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(link.href);
    },
    onError: () => {
      alert(t('counters_export_error_alert_message'));
    },
  });

  const {
    mutate: exportMutateExcel,
    isLoading: isExportingExcel,
    isSuccess: isExportExcelSuccess,
  } = useMultipleConsumptionExportExcel({
    onSuccess: (data: ArrayBuffer) => {
      let fileExtension = 'xlsx';
      const fileName = `ConsumptionExport-${exportFormat}-from-${fromDateTz.toISOString()}-to-${toDateTz.toISOString()}.${fileExtension}`;
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(link.href);
    },
    onError: () => {
      alert(t('counters_export_error_alert_message'));
    },
  });

  const formDeviceSensorsOptions = (): DeviceSensorsDto[] => {
    const deviceSensorOptions = selectedDevices.map((deviceNumber: string) => {
      return {
        deviceNumber: deviceNumber,
        sensorIndexes: selectedSensors.sort((a: number, b: number) => {
          return a - b;
        }),
      };
    });

    return deviceSensorOptions;
  };

  const downloadHandler = (): void => {
    const deviceSensors = formDeviceSensorsOptions();
    switch (exportFormat) {
      case 'CSV':
        exportMutateCSV({
          body: {
            deviceSensors: deviceSensors,
            deviceGroupIds: selectedDeviceGroups,
            fromDate: fromDateTz,
            toDate: toDateTz,
          },
          fetchType: aggregationPeriod || undefined,
        });
        break;
      case 'Excel':
        exportMutateExcel({
          body: {
            deviceSensors: deviceSensors,
            deviceGroupIds: selectedDeviceGroups,
            fromDate: fromDateTz,
            toDate: toDateTz,
          },
          fetchType: aggregationPeriod || undefined,
        });
        break;
      default:
        break;
    }
    GoogleAnalyticsService.triggerEvent(
      GaEventCategory.REPORTS_PAGE,
      GaEventAction.REPORT_PAGE_DOWNLOAD_CLICK,
      exportFormat
    );
  };

  const loadJsonDataHandler = (): void => {
    const deviceSensors = formDeviceSensorsOptions();
    jsonMutate({
      body: {
        deviceSensors: deviceSensors,
        deviceGroupIds: selectedDeviceGroups,
        fromDate: fromDateTz,
        toDate: toDateTz,
      },
      fetchType: aggregationPeriod || undefined,
    });
    setIsConsumptionReportAggregated(!!aggregationPeriod);
    GoogleAnalyticsService.triggerEvent(
      GaEventCategory.REPORTS_PAGE,
      GaEventAction.REPORT_PAGE_SEARCH_CLICK,
      exportFormat
    );
  };

  const disableButtons = (): boolean => {
    return (
      devicesQuery.isLoading ||
      deviceGroupsQuery.isLoading ||
      isJSONLoading ||
      isExportingCSV ||
      isExportingExcel
    );
  };

  const hasSelection = selectedDevices.length > 0 || selectedDeviceGroups.length > 0;
  const hasCompletedRequest = isJSONSuccess || isExportCSVSuccess || isExportExcelSuccess;
  const showInitialAlerts =
    !isJSONLoading && !isExportingCSV && !isExportingExcel && !hasCompletedRequest;
  const showEmptySelectionAlert = showInitialAlerts && !hasSelection;
  const showReadyAlert = showInitialAlerts && hasSelection;

  const handleSelectedDeviceNumberChange = (deviceNumber: string | null): void => {
    dispatch(actions.devSelected(deviceNumber ? [deviceNumber] : [], 'radio'));
  };

  const handleConsumptionRowClick = (params: GridRowParams<ConsumptionExportDto>): void => {
    const deviceNumber = params.row.deviceNumber || null;
    handleSelectedDeviceNumberChange(deviceNumber);

    if (deviceNumber) {
      setSelectedDeviceSnackbar(
        (currentValue: { open: boolean; deviceNumber: string | null; key: number }) => ({
          open: true,
          deviceNumber,
          key: currentValue.key + 1,
        })
      );
    }
  };

  return (
    <Header>
      <Container>
        <section className="flex justify-center mb-6">
          <TitleHeader title={t('consumption_export_header')} />
        </section>
        <section className="flex flex-col gap-3">
          <div className="flex flex-col lg:flex-row justify-between gap-3 flex-wrap">
            <div className="flex flex-col lg:flex-row gap-3">
              <ConnectedDatePicker period={PickerPeriod.FROM} className="w-full lg:w-[200px]" />
              <ConnectedDatePicker period={PickerPeriod.TO} className="w-full lg:w-[200px]" />
              <FormControl size="small" className="w-full lg:w-40">
                <InputLabel>{t('consumption_export_aggregation_period')}</InputLabel>
                <Select
                  value={aggregationPeriod || 'NONE'}
                  label={t('consumption_export_aggregation_period')}
                  onChange={(event: SelectChangeEvent<AggregationOptions | 'NONE'>): void => {
                    setAggregationPeriod(
                      event.target.value !== 'NONE'
                        ? (event.target.value as AggregationOptions)
                        : null
                    );
                  }}
                >
                  {Object.entries(aggregationSelectOptions).map(
                    ([key, value]: [string, string]) => (
                      <MenuItem key={key} value={key}>
                        {value}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                disableElevation
                startIcon={isJSONLoading ? <CircularProgress size={15} /> : <SearchIcon />}
                disabled={disableButtons()}
                onClick={loadJsonDataHandler}
                className="w-full lg:w-auto"
              >
                {t('counters_export_search')}
              </Button>
            </div>
            <div className="flex flex-col gap-3 lg:flex-row">
              <FormControl size="small" className="w-full lg:w-28">
                <InputLabel>{t('counters_export_format')}</InputLabel>
                <Select
                  value={exportFormat}
                  label={t('counters_export_format')}
                  onChange={(e: SelectChangeEvent<ExportFormats>): void => {
                    setExportFormat(e.target.value as ExportFormats);
                  }}
                >
                  <MenuItem value={'CSV'}>CSV</MenuItem>
                  <MenuItem value={'Excel'}>Excel</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                disableElevation
                startIcon={
                  isExportingCSV || isExportingExcel ? (
                    <CircularProgress size={15} />
                  ) : (
                    <FileDownloadIcon />
                  )
                }
                disabled={disableButtons()}
                onClick={downloadHandler}
                className="w-full lg:w-auto"
              >
                {t('counters_export_download')}
              </Button>
            </div>
          </div>
        </section>
        <section className="grid grid-cols-1 xl:grid-cols-2 gap-4 my-5">
          <div className="w-full">
            <MultiSelectAutocompleteWithActions
              label={t('counter_export_select_devices_label')}
              placeholder={t('counter_export_select_devices_label')}
              options={deviceOptions}
              selectedValues={selectedDevices}
              setSelectedValues={setSelectedDevices}
              disabled={disableButtons()}
              loading={devicesQuery.isLoading}
            />
          </div>
          <div className="w-full">
            <MultiSelectAutocompleteWithActions
              label={t('report_config_form_device_groups')}
              placeholder={t('report_config_form_device_groups')}
              options={deviceGroupOptions}
              selectedValues={selectedDeviceGroups}
              setSelectedValues={setSelectedDeviceGroups}
              disabled={disableButtons()}
              loading={deviceGroupsQuery.isLoading}
            />
          </div>
        </section>
        <section className={showEmptySelectionAlert || showReadyAlert ? 'my-5' : ''}>
          {showEmptySelectionAlert && (
            <Alert severity="info">
              <Typography variant="body2">
                <strong>{t('consumption_export_empty_selection_title')}</strong>{' '}
                {t('consumption_export_empty_selection_message')}
              </Typography>
            </Alert>
          )}
          {showReadyAlert && (
            <Alert severity="success">
              <Typography variant="body2">
                <strong>{t('consumption_export_ready_title')}</strong>{' '}
                {t('consumption_export_ready_message', {
                  devicesCount: selectedDevices.length,
                  groupsCount: selectedDeviceGroups.length,
                })}
              </Typography>
            </Alert>
          )}
        </section>
        <section>
          <div className="w-full my-5">
            {isJSONLoading ? (
              <CircularProgress />
            ) : (
              <>
                {isJSONSuccess && (
                  <ConsumptionTable
                    rows={data}
                    density="compact"
                    selectedDeviceNumber={selectedDeviceNumber}
                    onRowClick={handleConsumptionRowClick}
                    getRowId={(row: ConsumptionExportDto): string =>
                      row.deviceNumber +
                      row.sensorIndex +
                      row.firstReadingDate +
                      row.lastReadingDate
                    }
                    isAggregated={isConsumptionExportAggregated}
                  />
                )}
              </>
            )}
          </div>
        </section>
      </Container>
      <Snackbar
        key={selectedDeviceSnackbar.key}
        open={selectedDeviceSnackbar.open}
        autoHideDuration={3000}
        onClose={(): void =>
          setSelectedDeviceSnackbar(
            (currentValue: { open: boolean; deviceNumber: string | null; key: number }) => ({
              ...currentValue,
              open: false,
            })
          )
        }
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={(): void =>
            setSelectedDeviceSnackbar(
              (currentValue: { open: boolean; deviceNumber: string | null; key: number }) => ({
                ...currentValue,
                open: false,
              })
            )
          }
          severity="success"
          sx={{ width: '100%' }}
        >
          {selectedDeviceSnackbar.deviceNumber
            ? t('consumption_export_selected_device_toast', {
                deviceNumber: selectedDeviceSnackbar.deviceNumber,
              })
            : ''}
        </Alert>
      </Snackbar>
    </Header>
  );
};

interface ConsumptionExportProps {}

export default ConsumptionExport;
